// src/pages/EventsPages/RegistrationStatusMessage.js

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  EventBusy as EventBusyIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { formatDateForDisplay, getDaysUntil } from '../../utils/registrationStatusUtils';

const RegistrationStatusMessage = ({ 
  status, 
  message, 
  startDate, 
  endDate, 
  competenciaName,
  onBackToEvent 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getStatusConfig = () => {
    switch (status) {
      case 'not-started':
        const daysUntilStart = getDaysUntil(startDate);
        return {
          icon: <ScheduleIcon sx={{ fontSize: { xs: 40, md: 48 } }} />,
          color: theme.palette.info.main,
          backgroundColor: theme.palette.info.light,
          backgroundColorLight: `${theme.palette.info.main}15`,
          title: 'Inscripciones Próximamente',
          subtitle: message,
          details: startDate ? `Inician el ${formatDateForDisplay(startDate)}` : '',
          countdown: daysUntilStart > 0 ? `Faltan ${daysUntilStart} día${daysUntilStart !== 1 ? 's' : ''}` : 'Inician hoy',
          chipLabel: 'Próximamente',
          chipColor: 'info'
        };
      
      case 'closed':
        const daysSinceClosed = Math.abs(getDaysUntil(endDate));
        return {
          icon: <EventBusyIcon sx={{ fontSize: { xs: 40, md: 48 } }} />,
          color: theme.palette.error.main,
          backgroundColor: theme.palette.error.light,
          backgroundColorLight: `${theme.palette.error.main}15`,
          title: 'Inscripciones Cerradas',
          subtitle: message,
          details: endDate ? `Cerraron el ${formatDateForDisplay(endDate)}` : '',
          countdown: daysSinceClosed > 0 ? `Hace ${daysSinceClosed} día${daysSinceClosed !== 1 ? 's' : ''}` : 'Cerraron hoy',
          chipLabel: 'Cerrado',
          chipColor: 'error'
        };
      
      default:
        return {
          icon: <InfoIcon sx={{ fontSize: { xs: 40, md: 48 } }} />,
          color: theme.palette.grey[600],
          backgroundColor: theme.palette.grey[300],
          backgroundColorLight: `${theme.palette.grey[500]}15`,
          title: 'Estado de Inscripciones',
          subtitle: message || 'Información no disponible',
          details: '',
          countdown: '',
          chipLabel: 'Info',
          chipColor: 'default'
        };
    }
  };

  const config = getStatusConfig();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: { xs: '400px', md: '500px' },
        p: { xs: 2, md: 4 },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          background: `linear-gradient(135deg, ${config.backgroundColorLight} 0%, ${config.backgroundColor}30 100%)`,
          borderRadius: 4,
          p: { xs: 3, md: 5 },
          textAlign: 'center',
          maxWidth: 600,
          width: '100%',
          border: `2px solid ${config.backgroundColor}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${config.color}, ${config.backgroundColor})`,
          }
        }}
      >
        {/* Chip de estado */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <Chip
            label={config.chipLabel}
            color={config.chipColor}
            variant="filled"
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '0.875rem', md: '1rem' },
              px: 2,
              py: 0.5,
            }}
          />
        </Box>

        {/* Icono principal */}
        <Box
          sx={{
            color: config.color,
            mb: 3,
            display: 'flex',
            justifyContent: 'center',
            '& svg': {
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
            }
          }}
        >
          {config.icon}
        </Box>

        {/* Título principal */}
        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            color: config.color,
            fontSize: { xs: '1.75rem', md: '2.125rem' },
            mb: 2,
          }}
        >
          {config.title}
        </Typography>

        {/* Subtítulo */}
        <Typography
          variant="h6"
          color="text.secondary"
          gutterBottom
          sx={{
            fontSize: { xs: '1rem', md: '1.25rem' },
            mb: 3,
            fontWeight: 500,
          }}
        >
          {config.subtitle}
        </Typography>

        {/* Detalles con fecha */}
        {config.details && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mb: 2,
              flexWrap: 'wrap',
            }}
          >
            <CalendarIcon 
              sx={{ 
                color: 'text.secondary', 
                fontSize: { xs: 18, md: 20 } 
              }} 
            />
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 500,
              }}
            >
              {config.details}
            </Typography>
          </Box>
        )}

        {/* Contador/countdown */}
        {config.countdown && (
          <Typography
            variant="body2"
            sx={{
              color: config.color,
              fontWeight: 'bold',
              fontSize: { xs: '0.875rem', md: '1rem' },
              mb: 4,
              opacity: 0.8,
            }}
          >
            {config.countdown}
          </Typography>
        )}

        {/* Información adicional */}
        {competenciaName && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 4,
              fontStyle: 'italic',
              fontSize: { xs: '0.8rem', md: '0.875rem' },
            }}
          >
            {competenciaName}
          </Typography>
        )}

        {/* Botón de retorno */}
        {onBackToEvent && (
          <Button
            variant="outlined"
            onClick={onBackToEvent}
            sx={{
              borderColor: config.color,
              color: config.color,
              fontWeight: 'bold',
              px: { xs: 3, md: 4 },
              py: { xs: 1, md: 1.5 },
              borderRadius: 2,
              textTransform: 'none',
              fontSize: { xs: '0.875rem', md: '1rem' },
              '&:hover': {
                backgroundColor: config.backgroundColorLight,
                borderColor: config.color,
              },
            }}
          >
            Volver al Evento
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default RegistrationStatusMessage;